/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import {
    useCreateFinalSignDocs,
    useGetHtmlSignDocument,
    useSelectedPatient,
    useShowErrorPopup,
    useCreateSignEvent,
    useSelectedQuote,
    useSession,
    useUpdateQuote,
    useUpdatePatient,
    useCloneQuote,
    useUpdateDeal,
    usePatientActions,
} from "../../../hooks";
import { documentsData } from "../../../consts/pdf-documents/documents";
import ModalSignatureCanvas from "../../../components/Modals/modal-signature-canvas/modal-signature-canvas";
import { DocumentCategory } from "../../../consts/pdf-documents/documents";
import { dealPaymentTypes } from "../../../consts";
import { createPaymentList } from "../../../utils/create-payment-list";
import { GlobalService } from "../../../hexagonal-architecture-frontend-base/src/domain/services/Global.service";
import { Button } from "../../../components/Buttons/button";

import classNames from "classnames/bind";
import styles from "./new-html-sign-form.module.css";
import { useTranslation } from "react-i18next";

const interpolate = (str: string, params: Record<string, any>) => {
    return str.replace(/\${(\w+)}/g, (_, key) => {
        return params[key] !== undefined ? params[key] : `\${${key}}`;
    });
};

const cx = classNames.bind(styles);

const NewHtmlSignForm = () => {
    const [firstSign, setFirstSign] = useState<string | null>(null);
    const [secondSign, setSecondSign] = useState<string | null>(null);
    const [openSignModal, setOpenSignModal] = useState<boolean>(false);
    const [currentSlot, setCurrentSlot] = useState<number | null>(null);
    const [htmlDocumentContent, setHtmlDocumentContent] = useState<string>("");
    const [signRequestId, setSignRequestId] = useState<string>("");
    const [signatures, setSignatures] = useState<Record<number, string>>({});
    const [isDocumentEnabled, setIsDocumentEnabled] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);
    const [htmlDocumentData, setHtmlDocumentData] = useState<
        Record<string, string>
    >({});
    const [openTermsModal, setOpenTermsModal] = useState<boolean>(false);
    const [htmlDocumentCategory, setHtmlDocumentCategory] =
        useState<DocumentCategory | null>(null);
    const [showMessage, setShowMessage] = useState(false);
    const { t } = useTranslation();

    const [totalSignatureSlots, setTotalSignatureSlots] = useState<number[]>(
        []
    );
    const [isAccepting, setIsAccepting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { removeSelectedPatient } = useSelectedPatient();
    const { removeSelectedQuote } = useSelectedQuote();
    const { syncPatientsWithZoho } = usePatientActions();

    const viewedSent = useRef(false);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const acceptToastRef = useRef<string | null>(null);

    // REF PARA SOLUCIONAR PANTALLA BLANCA
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { userInfo } = useSession();
    const location = useLocation();

    const {
        iban,
        bicCode,
        totalAmount,
        firstPayment,
        totalAmountMonthly,
        numberOfPayments,
        financialCost,
        FEES,
    } = location.state || {};
    const { patientId, reportId, docId } = useParams();
    const { selectedPatient } = useSelectedPatient();
    const { createFinalSignDocs } = useCreateFinalSignDocs();
    const { createSignEvent } = useCreateSignEvent();
    const { showErrorPopup } = useShowErrorPopup();
    const { getHtmlSignDocument } = useGetHtmlSignDocument();
    const navigate = useNavigate();
    const { selectedQuote } = useSelectedQuote();
    const { updateQuote } = useUpdateQuote();
    const { updatePatient } = useUpdatePatient();
    const { updateDeal } = useUpdateDeal();
    const { cloneQuote } = useCloneQuote();

    const dataToHoldRef = useRef({
        consentId: selectedPatient.dealDetails.Archivo_Consentimientos?.length
            ? selectedPatient.dealDetails.Archivo_Consentimientos[0].id
            : "",
        contactConsentId: selectedPatient.Consentimientos?.length
            ? selectedPatient.Consentimientos[0].id
            : "",
        signedAuthorizations:
            selectedPatient.dealDetails.Autorizaciones_Firmadas,
        dealId: selectedPatient.dealDetails.id,
        dealName: selectedPatient.dealDetails.Deal_Name,
        patientId: selectedPatient.id,
        patientName: selectedPatient.title,
        patientCountryId: selectedPatient?.dni,
        accessToConsentSignDocument:
            selectedQuote?.Access_To_Consent_Sign_Document,
    });

    dataToHoldRef.current = {
        consentId: selectedPatient.dealDetails.Archivo_Consentimientos?.length
            ? selectedPatient.dealDetails.Archivo_Consentimientos[0].id
            : "",
        contactConsentId: selectedPatient.Consentimientos?.length
            ? selectedPatient.Consentimientos[0].id
            : "",
        signedAuthorizations:
            selectedPatient.dealDetails.Autorizaciones_Firmadas,
        dealId: selectedPatient.dealDetails.id,
        dealName: selectedPatient.dealDetails.Deal_Name,
        patientId: selectedPatient.id,
        patientName: selectedPatient.title,
        patientCountryId: selectedPatient?.dni,
        accessToConsentSignDocument:
            selectedQuote?.Access_To_Consent_Sign_Document,
    };

    const handleSelectPayMethod = () => {
        if (reportId) {
            updateQuote.receipt(
                reportId,
                numberOfPayments,
                totalAmount,
                totalAmount,
                true
            );
            if(Number(numberOfPayments) > 2) {
                cloneQuote.receipt({
                    quoteId: reportId,
                    subject: `Cloned - ${selectedQuote?.Subject}`,
                    stage: "Presupuesto aceptado",
                    reportType: "Financiación",
                    acceptedAmount: Number((FEES + financialCost).toFixed(2)),
                    payment1: Number((FEES + financialCost).toFixed(2)),
                    numberOfPayments: "1",
                    quotedItems: [
                        {
                            productData: {
                                id: "859620000051230683",
                                name: "Gastos de Gestión",
                                code: "GG",
                            },
                            quantity: 1,
                            listPrice: FEES,
                        },
                        {
                            productData: {
                                id: "859620000278398147",
                                name: "Gastos de Financiación",
                                code: "Gf",
                            },
                            quantity: 1,
                            listPrice: financialCost,
                        },
                    ],
                    uploadToSage: true,
                    dateUploadToSage: new Date().toISOString().substring(0, 10),
                    zone: `FIN-${selectedQuote?.Plaza_Residencia}`,
                    initialPaymentDate: new Date().toISOString().substring(0, 10),
                });
            }
        }
        finishPayment();
    };

    const finishPayment = () => {
        updatePatient({
            id: patientId as string,
            iban: iban,
            swiftBic: bicCode,
        });
        updateDeal.receipt(
            dataToHoldRef.current.dealId,
            dataToHoldRef.current.dealName,
            {
                App_Consumer_Payment_Managed: true,
                Pago_Inicial: true,
                Pago_Final: true,
                Tipo_de_Pago: dealPaymentTypes.RECEIPT,
                Signed_SEPA: true,
                Pagos: createPaymentList(
                    Number(numberOfPayments),
                    dataToHoldRef.current.dealId,
                    {
                        onePaymentTotalAmount: totalAmount,
                        installmentFirstPayment: firstPayment,
                        installmentMonthlyPaymentAmount: totalAmountMonthly,
                        installmentTotalAmount: totalAmountMonthly * (Number(numberOfPayments) - 1),
                    },
                    userInfo?.tutorFullName as string
                ),
            },
            {
                onSuccess: async () => {
                    await syncPatientsWithZoho()
                    setShowMessage(true);
                },
            }
        );
    };

    useEffect(() => {
        // ESTA ES LA LÍNEA QUE ELIMINA LOS 2 LOGS DE UNDEFINED
        if (!htmlDocumentCategory || !selectedQuote?.id) return;

        const quoteId = selectedQuote.id;

        if (htmlDocumentCategory === "INFORMED_CONSENT") {
            if (!dataToHoldRef.current.accessToConsentSignDocument) {
                updateQuote.accessToConsentSignDocument(quoteId, true);
            }
        } else if (htmlDocumentCategory === "SEPA") {
            updateQuote.accessToSepaSignDocument(quoteId, true);
        }

        // Solo escuchamos el ID y la categoría
    }, [selectedQuote?.id, htmlDocumentCategory]);

    const spinnerSvg = `<div style="display: flex; justify-content: center; align-items: center; width: 100%; height: 100%;"><svg class="animate-spin" style="animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24"><circle style="opacity: 0.25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path style="opacity: 0.75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg></div><style>@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }</style>`;

    /* =========================
       METADATA CON TIMEOUT (Para evitar bloqueo de pestaña)
    ========================= */
    const getClientMetadata = async () => {
        let geoLocation: any = {};
        let ipAddress = "";
        const userAgent = navigator.userAgent;

        const getGeo = () =>
            new Promise((resolve) => {
                if (!navigator.geolocation) return resolve({});
                const timeout = setTimeout(() => resolve({}), 2500);
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        clearTimeout(timeout);
                        resolve({
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                        });
                    },
                    () => {
                        clearTimeout(timeout);
                        resolve({});
                    },
                    { timeout: 2500 }
                );
            });

        try {
            const [geo, ipRes] = await Promise.all([
                getGeo(),
                fetch("https://api.ipify.org?format=json")
                    .then((res) => res.json())
                    .catch(() => ({ ip: "" })),
            ]);
            geoLocation = geo;
            ipAddress = ipRes.ip || "";
        } catch (error) {
            console.error("Error retrieving metadata: ", error);
        }

        return { geoLocation, ipAddress, userAgent };
    };

    /* =========================
       CORRECCIÓN PANTALLA BLANCA (Repaint al recuperar foco)
    ========================= */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState === "visible" &&
                scrollContainerRef.current
            ) {
                const container = scrollContainerRef.current;
                container.scrollTop += 1;
                setTimeout(() => {
                    if (container) container.scrollTop -= 1;
                }, 50);
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);
        return () =>
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
    }, []);

    /* =========================
       SCROLL MONITOR
    ========================= */
    useEffect(() => {
        if (
            viewedSent.current ||
            !signRequestId ||
            !htmlDocumentContent ||
            !selectedQuote?.id
        )
            return;
        const lastSlot =
            totalSignatureSlots.length > 0
                ? Math.max(...totalSignatureSlots)
                : 1;
        const sentinel = document.querySelector(
            `[data-signature="${lastSlot}"]`
        );

        if (sentinel) {
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0].isIntersecting && !viewedSent.current) {
                        viewedSent.current = true;
                        setTimeout(async () => {
                            const { geoLocation, ipAddress, userAgent } =
                                await getClientMetadata();
                            createSignEvent({
                                signRequestId,
                                eventType: "VIEWED",
                                ipAddress,
                                userAgent,
                                geoLocation,
                                metadata: {},
                                quoteId: selectedQuote.id,
                                patientName: selectedPatient.title,
                                email: userInfo?.email as string,
                            });
                        }, 0);
                        observerRef.current?.disconnect();
                    }
                },
                { threshold: 0 }
            );
            observerRef.current.observe(sentinel);
        }
        return () => observerRef.current?.disconnect();
    }, [
        signRequestId,
        htmlDocumentContent,
        totalSignatureSlots,
        selectedQuote?.id,
    ]);

    /* =========================
       LOAD DOCUMENT
    ========================= */
    useEffect(() => {
        const getHtmlSignDocumentInfo = async () => {
            if (!(docId && selectedQuote?.id)) return;
            const { geoLocation, ipAddress, userAgent } =
                await getClientMetadata();
            getHtmlSignDocument(
                {
                    docId: docId as string,
                    locale: "es-ES",
                    ipAddress,
                    userAgent,
                    geoLocation,
                    metadata: {},
                    quoteId: selectedQuote?.id as string,
                    patientName: selectedPatient.title,
                    tutorEmail: userInfo?.email as string,
                },
                {
                    onError: () => showErrorPopup("errors.signRequestFailed"),
                    onSuccess: (htmlSignDocumentData) => {
                        let updatedHtml = htmlSignDocumentData.htmlContent;
                        const slotsFound: number[] = [];
                        const regexSearch = /data-signature="(\d+)"/gi;
                        let match;
                        while (
                            (match = regexSearch.exec(updatedHtml)) !== null
                        ) {
                            slotsFound.push(Number(match[1]));
                        }
                        if (slotsFound.length === 0) {
                            updatedHtml = updatedHtml.replace(
                                /<\/section>/i,
                                `<div data-signature="1" class="signature-placeholder">FIRMA</div></section>`
                            );
                            slotsFound.push(1);
                        }
                        setTotalSignatureSlots(slotsFound);
                        setHtmlDocumentData(htmlSignDocumentData.data);
                        setHtmlDocumentCategory(htmlSignDocumentData.category);
                        setHtmlDocumentContent(updatedHtml);
                        setSignRequestId(htmlSignDocumentData.signRequestId);
                    },
                }
            );
        };
        getHtmlSignDocumentInfo();
    }, [docId, selectedQuote?.id, dataToHoldRef.current.patientName]);

    /* =========================
       DOM SYNC (isDocumentEnabled)
    ========================= */
    useEffect(() => {
        if (isDocumentEnabled) {
            const consentSection = document.querySelector(
                ".check-confirm-section"
            ) as HTMLElement;
            if (consentSection) consentSection.style.display = "none";
            const mainSection = document.querySelector("main") as HTMLElement;
            if (mainSection) mainSection.style.pointerEvents = "auto";
        }
    }, [htmlDocumentContent, isDocumentEnabled]);

    /* =========================
       HANDLE CONSENT ACCEPT
    ========================= */
    const handleConsentAccept = async () => {
        setIsAccepting(true);
        const btn = document.querySelector(
            ".accept-continue-button"
        ) as HTMLButtonElement;
        if (btn) {
            const rect = btn.getBoundingClientRect();
            btn.style.width = `${rect.width}px`;
            btn.style.height = `${rect.height}px`;
            const originalText = btn.innerHTML;
            btn.innerHTML = spinnerSvg;

            const { geoLocation, ipAddress, userAgent } =
                await getClientMetadata();

            createSignEvent(
                {
                    signRequestId,
                    eventType: "ACCEPTED",
                    ipAddress,
                    userAgent,
                    geoLocation,
                    metadata: {},
                    quoteId: selectedQuote?.id,
                    patientName: selectedPatient.title,
                    email: userInfo?.email as string,
                },
                {
                    onError: () => {
                        showErrorPopup("errors.consentFailed");
                        setIsAccepting(false);
                        btn.innerHTML = originalText;
                        btn.style.width = "";
                        btn.style.height = "";
                    },
                    onSuccess: () => {
                        const consentSection = document.querySelector(
                            ".check-confirm-section"
                        ) as HTMLElement;
                        if (consentSection)
                            consentSection.style.display = "none";
                        const mainSection = document.querySelector(
                            "main"
                        ) as HTMLElement;
                        if (mainSection)
                            mainSection.style.pointerEvents = "auto";
                        setIsDocumentEnabled(true);
                        setIsAccepting(false);
                    },
                }
            );
        }
    };

    /* =========================
       DELEGATED EVENT HANDLER
    ========================= */
    useEffect(() => {
        if (!htmlDocumentContent) return;

        const clickHandler = async (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            if (target.matches(".terms-conditions-link")) {
                e.preventDefault();
                setOpenTermsModal(true);
                return;
            }

            const placeholder = target.closest(
                "[data-signature]"
            ) as HTMLElement | null;
            if (placeholder) {
                const slot = Number(placeholder.getAttribute("data-signature"));
                if (!slot || signatures[slot]) return;
                const firstAvailableSignature = Object.values(signatures)[0];

                if (firstAvailableSignature) {
                    const base64 = firstAvailableSignature;
                    setTimeout(async () => {
                        const { geoLocation, ipAddress, userAgent } =
                            await getClientMetadata();
                        createSignEvent({
                            signRequestId,
                            eventType: "SIGNED",
                            ipAddress,
                            userAgent,
                            geoLocation,
                            metadata: { duplicatedSlot: slot },
                            patientName: selectedPatient.title,
                            quoteId: selectedQuote?.id,
                            email: userInfo?.email as string,
                        });
                    }, 0);

                    setSignatures((prev) => ({ ...prev, [slot]: base64 }));
                    if (slot === 1) setFirstSign(base64);
                    if (slot === 2) setSecondSign(base64);

                    const remainingSlots = totalSignatureSlots.filter((s) => s !== slot && !signatures[s]);
                    const isLastSign = remainingSlots.length === 0;

                    const toastId = toast(
                        (t) => (
                            <span className={"toast-span"}>
                                <button
                                    className={"toast-button"}
                                    style={{
                                        color: "#fff",
                                        background: "#00aa00",
                                        border: "none",
                                        padding: "4px 8px",
                                        cursor: "pointer",
                                        borderRadius: 4,
                                        width: "100%",
                                    }}
                                    onClick={() => {
                                        const selector = isLastSign
                                            ? "#submit-btn"
                                            : `[data-signature="${remainingSlots[0]}"]`;
                                        const el =
                                            document.querySelector(selector);
                                        if (el)
                                            el.scrollIntoView({
                                                behavior: "smooth",
                                                block: "center",
                                            });
                                        toast.dismiss(toastId);
                                    }}
                                >
                                    {isLastSign
                                        ? "Ir al final del documento >>"
                                        : "Ir a la siguiente firma >>"}
                                </button>
                            </span>
                        ),
                        { duration: Infinity, style: { background: "#00aa00" } }
                    );
                    return;
                }
                setCurrentSlot(slot);
                setOpenSignModal(true);
                return;
            }

            if (target.matches(".accept-continue-button")) {
                if (isAccepting) return;
                const checkbox = document.querySelector(
                    ".consent-checkbox"
                ) as HTMLInputElement | null;
                if (checkbox?.checked) handleConsentAccept();
                return;
            }

            if (target.id === "submit-btn") {
                if (isSubmitting) return;
                submitDocument();
            }
        };

        const changeHandler = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target.matches(".consent-checkbox")) {
                setAcceptedTerms(target.checked);
                const acceptButton = document.querySelector(
                    ".accept-continue-button"
                ) as HTMLButtonElement | null;
                if (acceptButton) acceptButton.disabled = !target.checked;
            }
        };

        document.addEventListener("click", clickHandler);
        document.addEventListener("change", changeHandler);
        return () => {
            document.removeEventListener("click", clickHandler);
            document.removeEventListener("change", changeHandler);
        };
    }, [
        htmlDocumentContent,
        signatures,
        acceptedTerms,
        isAccepting,
        isSubmitting,
        totalSignatureSlots,
    ]);

    /* =========================
       TOAST ACCEPT SECTION
    ========================= */
    useEffect(() => {
        const section = document.querySelector(
            ".check-confirm-section"
        ) as HTMLElement;
        const scrollContainer = scrollContainerRef.current;
        if (!section || !scrollContainer) return;

        const onScroll = () => {
            if (isDocumentEnabled) {
                if (acceptToastRef.current) {
                    toast.dismiss(acceptToastRef.current);
                    acceptToastRef.current = null;
                }
                return;
            }
            const sectionRect = section.getBoundingClientRect();
            const containerRect = scrollContainer.getBoundingClientRect();
            const isVisible =
                sectionRect.top < containerRect.bottom &&
                sectionRect.bottom > containerRect.top;

            if (!isVisible) {
                if (acceptToastRef.current) return;
                const toastId = toast(
                    () => (
                        <span
                            style={{
                                background: "#00aa00",
                                color: "#fff",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "15px",
                                width: "100%",
                                minWidth: "200px",
                                padding: "15px",
                                borderRadius: "8px",
                            }}
                        >
                            <button
                                style={{
                                    color: "#fff",
                                    background: "#00aa00",
                                    border: "none",
                                    padding: "6px 12px",
                                    cursor: "pointer",
                                    borderRadius: 4,
                                    width: "100%",
                                    fontWeight: "bold",
                                }}
                                onClick={() => {
                                    section.scrollIntoView({
                                        behavior: "smooth",
                                        block: "center",
                                    });
                                    toast.dismiss(toastId);
                                    acceptToastRef.current = null;
                                }}
                            >
                                Acepta para firmar. <br /> Ir &gt;&gt;
                            </button>
                        </span>
                    ),
                    {
                        duration: Infinity,
                        position: "bottom-center",
                        style: { background: "#00aa00", padding: 0 },
                    }
                );
                acceptToastRef.current = toastId;
            } else {
                if (acceptToastRef.current) {
                    toast.dismiss(acceptToastRef.current);
                    acceptToastRef.current = null;
                }
            }
        };

        const initialCheck = setTimeout(onScroll, 100);
        scrollContainer.addEventListener("scroll", onScroll);
        return () => {
            clearTimeout(initialCheck);
            scrollContainer.removeEventListener("scroll", onScroll);
            if (acceptToastRef.current) {
                toast.dismiss(acceptToastRef.current);
                acceptToastRef.current = null;
            }
        };
    }, [isDocumentEnabled, htmlDocumentContent]);

    /* =========================
       UPDATE HTML WITH SIGNATURES
    ========================= */
    useEffect(() => {
        if (!htmlDocumentContent) return;
        let updatedHtml = htmlDocumentContent;
        Object.entries(signatures).forEach(([slot, base64]) => {
            const regex = new RegExp(`<div[^>]*data-signature="${slot}"[^>]*>FIRMA</div>`,"i");
            updatedHtml = updatedHtml.replace(regex,`<img data-signature="${slot}" src="${base64}" class="signed-image-${slot}" />`);
        });
        const allSigned =
            totalSignatureSlots.length > 0 &&
            totalSignatureSlots.every((slot) => !!signatures[slot]);
        if (allSigned) {
            updatedHtml = updatedHtml.replace(
                /<button([^>]*id="submit-btn"[^>]*)hidden([^>]*)>/i,
                `<button$1$2>`
            );
        }
        setHtmlDocumentContent(updatedHtml);
    }, [signatures, totalSignatureSlots]);

    /* =========================
       HANDLE SIGNATURE COMPLETION
    ========================= */
    const handleSignComplete = (base64: string) => {
        if (!currentSlot) return;
        const isFirstSignatureEver = Object.keys(signatures).length === 0;

        setTimeout(async () => {
            const { geoLocation, ipAddress, userAgent } =
                await getClientMetadata();
            createSignEvent({
                signRequestId,
                eventType: "SIGNED",
                ipAddress,
                userAgent,
                geoLocation,
                metadata: { slot: currentSlot },
                quoteId: selectedQuote?.id,
                patientName: selectedPatient.title,
                email: userInfo?.email as string,
            });
        }, 0);

        setSignatures((prev) => ({ ...prev, [currentSlot]: base64 }));
        if (currentSlot === 1) setFirstSign(base64);
        if (currentSlot === 2) setSecondSign(base64);
        setOpenSignModal(false);

        if (isFirstSignatureEver && totalSignatureSlots.length > 1) {
            const otherSlot = totalSignatureSlots.find(
                (s) => s !== currentSlot
            );
            const toastId = toast(
                (t) => (
                    <div>
                        <span
                            className={"toastSpan"}
                            style={{
                                background: "#00aa00",
                                color: "#fff",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "15px",
                                width: "100%",
                                minWidth: "200px",
                                padding: "15px",
                                borderRadius: "8px",
                            }}
                        >
                            <button
                                style={{
                                    marginLeft: 8,
                                    color: "#fff",
                                    background: "#00aa00",
                                    border: "none",
                                    padding: "4px 8px",
                                    cursor: "pointer",
                                    borderRadius: 4,
                                }}
                                onClick={() => {
                                    const el = document.querySelector(
                                        `[data-signature="${otherSlot}"]`
                                    );
                                    if (el)
                                        el.scrollIntoView({
                                            behavior: "smooth",
                                            block: "center",
                                        });
                                    toast.dismiss(toastId);
                                }}
                            >
                                Ir a la otra firma &gt;&gt;
                            </button>
                        </span>
                    </div>
                ),
                { duration: Infinity, style: { background: "#00aa00" } }
            );
        }
    };

    /* =========================
       SUBMIT DOCUMENT
    ========================= */
    const submitDocument = async () => {
        if (!htmlDocumentCategory) return;
        setIsSubmitting(true);
        const btn = document.getElementById("submit-btn") as HTMLButtonElement;
        if (btn) {
            const rect = btn.getBoundingClientRect();
            btn.style.width = `${rect.width}px`;
            btn.style.height = `${rect.height}px`;
            const originalText = btn.innerHTML;
            btn.innerHTML = spinnerSvg;

            const { geoLocation, ipAddress, userAgent } =
                await getClientMetadata();
            const documentData = documentsData[htmlDocumentCategory]["es-ES"];
            documentData.data = { ...htmlDocumentData };

            const availableData: Record<string, any> = {
                reportId,
                docId,
                signRequestId,
                ...htmlDocumentData,
                ...dataToHoldRef.current,
                documentCategory: htmlDocumentCategory,
            };

            createFinalSignDocs(
                {
                    documentCategory: htmlDocumentCategory,
                    data: { ...documentData.data },
                    zohoDealId: dataToHoldRef.current.dealId,
                    zohoDealName: dataToHoldRef.current.dealName,
                    docId: documentData.documentId,
                    signRequestId,
                    ipAddress,
                    userAgent,
                    geoLocation,
                    metadata: {},
                    signatures: Object.keys(signatures).reduce((acc, key) => {
                        acc[key] = signatures[Number(key)];
                        return acc;
                    }, {} as Record<string, string>),
                    locale: "es-ES",
                    acceptedTerms,
                },
                {
                    onError: () => {
                        showErrorPopup("errors.signRequestFailed");
                        setIsSubmitting(false);
                        btn.innerHTML = originalText;
                        btn.style.width = "";
                        btn.style.height = "";
                        navigate(
                            `/informeDetallado/${patientId}/${reportId}/confirmation`
                        );
                    },
                    onSuccess: () => {
                        setIsSubmitting(false);
                        const interpolateUrl = interpolate(
                            documentData.finalUrl,
                            availableData
                        );
                        if (htmlDocumentCategory === "SEPA")
                            handleSelectPayMethod();
                        else navigate(interpolateUrl);
                    },
                }
            );
        }
    };

    const handleFinalizePayment = () => {
        removeSelectedQuote();
        removeSelectedPatient();
        navigate("/personas");
    };

    return (
        <>
            <Toaster position="bottom-center" />
            {showMessage ? (
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "white",
                        width: "100%",
                        height: "90vh",
                        padding: "20px",
                        boxSizing: "border-box",
                    }}
                >
                    <p
                        style={{
                            fontSize: "larger",
                            marginBottom: "20px",
                            fontWeight: "900",
                            textAlign: "center",
                        }}
                    >
                        {GlobalService.uppercaseFirstLetter(
                            t(
                                "modal.transferencia.confirmTransferMethodMessage"
                            )
                        )}
                    </p>
                    <Button onClick={handleFinalizePayment} fullWidth>
                        {t("confirm")}
                    </Button>
                </div>
            ) : (
                <div
                    ref={scrollContainerRef}
                    className="form-scroll-wrapper"
                    style={{
                        height: "calc(100vh - 80px)",
                        marginTop: window.innerWidth >= 769 ? "-48px" : "0px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        width: "100%",
                        backgroundColor: "#fff",
                        display: "block",
                    }}
                >
                    <style>
                        {`
                        .form-scroll-wrapper { margin-top: -50px; width: 100%; background-color: #fff; display: block; position: relative; overflow-x: hidden; overflow-y: scroll; }
                        .main-section { height: auto !important; min-height: 100% !important; display: block !important; overflow: visible !important; }
                        .doc-content, .doc-container { height: auto !important; display: block !important; }
                        @media (min-width: 769px) {
                          .form-scroll-wrapper { height: calc(100vh - 80px); }
                          .main-section { width: 90% !important; margin: 0 auto !important; padding-top: 30px; padding-bottom: 150px; }
                          .form-scroll-wrapper::-webkit-scrollbar { width: 16px !important; display: block !important; }
                          .form-scroll-wrapper::-webkit-scrollbar-track { background: #f0f0f0 !important; border-left: 1px solid #e0e0e0; }
                          .form-scroll-wrapper::-webkit-scrollbar-thumb { background-color: #888 !important; border-radius: 10px !important; border: 4px solid #f0f0f0 !important; min-height: 80px !important; }
                        }
                        @media (max-width: 768px) {
                          .form-scroll-wrapper { height: calc(100vh - 60px); -webkit-overflow-scrolling: touch; }
                          .main-section { width: 95% !important; margin: 0 auto !important; padding-top: 15px; padding-bottom: 120px; }
                        }
                        `}
                    </style>
                    <div className="doc-container" style={{ position: "relative", width: "100%" }}>
                        <div className="doc-content" dangerouslySetInnerHTML={{__html: htmlDocumentContent}}/>
                        {openTermsModal && (
                            <div className={styles.signModalBackdrop}>
                                <div className={styles.signModal}>
                                    <h2 className={styles.modalTitle}>Divulgación de registros y firmas electrónicos</h2>
                                    
                                    <div className={styles.signLegalText}>
                                        <p>Al hacer clic en "Aceptar", usted reconoce y acepta lo siguiente:</p>
                                        
                                        <p><strong>1. Consentimiento para el uso de medios electrónicos:</strong> Usted acepta voluntariamente que los acuerdos, formularios, autorizaciones y demás documentos relacionados con nuestros servicios sean presentados, firmados y almacenados de forma electrónica.</p>
                                        
                                        <p><strong>2. Validez legal:</strong> De acuerdo con el Reglamento (UE) Nº 910/2014 (eIDAS), las firmas electrónicas realizadas a través de este sistema tienen plenos efectos jurídicos, siendo equivalentes a su firma manuscrita.</p>
                                        
                                        <p><strong>3. Integridad del documento:</strong> El sistema garantiza la integridad del documento firmado, siendo cualquier alteración posterior detectable.</p>
                                        
                                        <p><strong>4. Derechos del usuario:</strong> Puede retirar su consentimiento en cualquier momento contactando a info@luracare.es.</p>
                                        
                                        <p><strong>5. Acceso y copias:</strong> Podrá acceder y descargar una copia de los documentos firmados desde su área personal.</p>
                                        
                                        <p><strong>Lura Care S.L.U.</strong> asegura el tratamiento seguro de sus datos personales conforme al RGPD.</p>
                                    </div>

                                    <div className={styles.signModalActions}>
                                        <button 
                                            className="primary" 
                                            onClick={() => setOpenTermsModal(false)}
                                        >
                                            Aceptar
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                        {!isDocumentEnabled && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "100%",
                                    background: "transparent",
                                    zIndex: 50,
                                    pointerEvents: "auto",
                                }}
                            />
                        )}
                    </div>
                </div>
            )}
            {openSignModal && (
                <ModalSignatureCanvas
                    slot={currentSlot}
                    onComplete={handleSignComplete}
                    onCancel={() => setOpenSignModal(false)}
                />
            )}
        </>
    );
};

export default NewHtmlSignForm;
